/**
 * Service layer for ClubOnboardingWizard. Two implementations:
 *
 *  - createTenantService(): hits the tenant-side /api/club-onboarding/* endpoints.
 *    Used at /admin/club-onboarding by an already-provisioned tenant.
 *
 *  - createPlatformAdminService(setupId): hits the control-plane staging
 *    /api/admin/pending-tenant-setups/:id endpoints. Used at
 *    /platform-admin/new-tenant-setup BEFORE the tenant exists. Data lives in
 *    pending_tenant_setups until POST /:id/provision creates the tenant and
 *    hydrates its venue_configuration.
 *
 * Both services expose the same 5-method shape consumed by the wizard:
 *   { load, saveDraft, saveBusiness, savePolicy, complete }
 */
import { getLocalApiUrl, authFetch } from '../../config/api'

/** Top-level list columns for pending_tenant_setups from wizard business fields. */
function businessListPatchFields(business) {
  const patch = {}
  if (!business || typeof business !== 'object') return patch
  const display = (business.name || business.legal_name || '').trim()
  if (display) patch.display_name = display
  const slug = String(business.groupCode || '').trim()
  if (slug) patch.tenant_slug = slug
  return patch
}

// ──────────────────────────────────────────────────────────────────────────
// Tenant service — mirrors the original inline fetches one-to-one
// ──────────────────────────────────────────────────────────────────────────
export function createTenantService() {
  const url = (p) => getLocalApiUrl(p)
  return {
    mode: 'tenant',

    async load() {
      const r = await authFetch(url('/api/club-onboarding'))
      return r.json()
    },

    async saveDraft({ step, draft }) {
      const r = await authFetch(url('/api/club-onboarding/draft'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ step, draft }),
      })
      const j = await r.json().catch(() => ({}))
      if (!r.ok || j.success === false) {
        throw new Error(j.error || `Failed to save draft (${r.status})`)
      }
      return j
    },

    async saveBusiness(business) {
      const r = await authFetch(url('/api/club-onboarding/business'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(business),
      })
      return r.json()
    },

    async savePolicy(body) {
      const r = await authFetch(url('/api/club-onboarding/policy'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      return r.json()
    },

    async complete(body) {
      const r = await authFetch(url('/api/club-onboarding/complete'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      return r.json()
    },
  }
}

// ──────────────────────────────────────────────────────────────────────────
// Platform-admin service — talks to pending_tenant_setups (staging)
// Loads/saves are partial PUTs against the JSONB columns. `complete` calls
// /:id/provision which (a) invokes /api/admin/tenants/provision and
// (b) hydrates operational_config into the new tenant DB.
// ──────────────────────────────────────────────────────────────────────────
export function createPlatformAdminService(setupId) {
  const base = `/api/admin/pending-tenant-setups/${encodeURIComponent(setupId)}`
  const url = (p) => getLocalApiUrl(p)

  async function patch(body) {
    const r = await authFetch(url(base), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const j = await r.json().catch(() => ({}))
    if (!r.ok || j.success === false) {
      throw new Error(j.error || `Save failed (${r.status})`)
    }
    return j
  }

  function venuesWithActivation(venues, activateVenueIds) {
    if (!Array.isArray(venues)) return venues
    const activeIds = new Set(activateVenueIds || [])
    return venues.map((v) => ({
      ...v,
      active: activeIds.has(v.venue_id),
    }))
  }

  /** Map wizard draft blob → pending_tenant_setups JSONB columns. */
  function draftToDbColumns(draft) {
    if (!draft || typeof draft !== 'object') return {}
    const out = {}
    if (draft.business != null) out.business_profile = draft.business
    if (draft.venues != null) {
      out.venues = venuesWithActivation(draft.venues, draft.activateVenueIds)
    }
    if (draft.weeklyTrading != null) out.weekly_trading = draft.weeklyTrading
    if (draft.egms != null) out.egms = draft.egms
    if (draft.operationalConfig != null) out.operational_config = draft.operationalConfig
    if (draft.groupSettings != null) out.group_settings = draft.groupSettings
    if (draft.sharedPayoutPolicy != null) out.shared_payout_policy = draft.sharedPayoutPolicy
    return out
  }

  return {
    mode: 'platform-admin',
    setupId,

    async load() {
      const r = await authFetch(url(base))
      if (!r.ok) return { success: false, error: `HTTP ${r.status}` }
      const j = await r.json()
      if (!j.success) return j
      const s = j.setup

      // Translate staging row → the shape the tenant `/api/club-onboarding`
      // endpoint returns, so the wizard component doesn't care which side it's on.
      const draft = s.draftJson || {}
      const data = {
        status: s.status === 'provisioned' ? 'complete' : 'incomplete',
        setupStatus: s.status || 'draft',
        provisioningError: s.provisioningError || null,
        provisionedGroupId: s.provisionedGroupId || null,
        groupId: s.tenantSlug || '',
        step: s.wizardStep || 0,
        business: s.businessProfile || draft.business || null,
        groupInfo: null,
        venues: s.venues || draft.venues || [],
        weeklyTrading: s.weeklyTrading || draft.weeklyTrading || {},
        operationalConfig: s.operationalConfig || draft.operationalConfig || null,
        sharedPayoutPolicy: s.sharedPayoutPolicy || draft.sharedPayoutPolicy || null,
        groupSettings: s.groupSettings || draft.groupSettings || null,
        egms: s.egms || draft.egms || [],
        activateVenueIds: draft.activateVenueIds || [],
        sharedVenue: draft.sharedVenue || null,
        draft, // for backwards-compat with wizard's load() path
      }
      return { success: true, data }
    },

    async saveDraft({ step, draft }) {
      // Persist full wizard state: draft_json plus typed JSONB columns used at provision.
      return patch({
        wizard_step: step,
        draft_json: draft,
        ...draftToDbColumns(draft),
        ...businessListPatchFields(draft?.business),
      })
    },

    /** Lightweight PATCH for list title/slug while the operator is still typing. */
    async syncListLabel(business) {
      const fields = businessListPatchFields(business)
      if (!Object.keys(fields).length) return { success: true }
      return patch(fields)
    },

    async saveBusiness(business) {
      return patch({
        business_profile: business,
        ...businessListPatchFields(business),
      })
    },

    async savePolicy(body) {
      return patch({
        operational_config: body.operationalConfig || null,
        group_settings:    body.groupSettings || null,
        shared_payout_policy: body.sharedPayoutPolicy || null,
      })
    },

    async complete(body) {
      // Final save of all sections, then trigger provisioning.
      // The actual tenant creation needs admin credentials; for now the wizard
      // saves all state and marks submitted — the provision step is a separate
      // explicit action on the list page (so the admin can review before
      // creating the tenant).
      await patch({
        business_profile:    body.business || null,
        venues:              venuesWithActivation(body.venues, body.activateVenueIds) || null,
        weekly_trading:      body.weeklyTrading || null,
        egms:                body.egms || null,
        operational_config:  body.operationalConfig || null,
        group_settings:      body.groupSettings || null,
        shared_payout_policy: body.sharedPayoutPolicy || null,
        draft_json: {
          business: body.business,
          venues: body.venues,
          weeklyTrading: body.weeklyTrading,
          egms: body.egms,
          operationalConfig: body.operationalConfig,
          groupSettings: body.groupSettings,
          sharedPayoutPolicy: body.sharedPayoutPolicy,
          activateVenueIds: body.activateVenueIds,
        },
        ...businessListPatchFields(body.business),
      })
      const r = await authFetch(url(`${base}/submit`), { method: 'POST' })
      const j = await r.json().catch(() => ({}))
      if (!r.ok && !j.success) {
        throw new Error(j.error || `Submit failed (${r.status})`)
      }
      return j
    },
  }
}
